import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "./Constant";
import { Box, Typography } from "@mui/material";
import CroswordCreatorGrid from "./CrosswordCreationGrid";
import { DndProvider, useDrag } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";

// Draggable Question Component
const DraggableQuestion = ({ question, setClickedQuestion, setTabIndex }) => {
  const [isDraggable, setIsDraggable] = useState(
    question.row_index === null || question.col_index === null,
  );

  const [, drag] = useDrag(() => ({
    type: "question",
    item: { question },
    canDrag: isDraggable,
  }));

  const handleEdit = () => {
    setTabIndex(0);
    setClickedQuestion(question);
    console.log("clicked question", question);
  };

  const renderAttachment = (url) => {
    if (!url) return null;

    // Handle Image Thumbnail
    if (
      url.match(
        /\.(apng|png|avif|gif|jpg|jpeg|jfif|pjpeg|pjp|svg|webp|pms|svg)$/i,
      )
    ) {
      return (
        <img
          key={url}
          src={url}
          alt="Attachment"
          style={{
            width: "60px",
            height: "60px",
            objectFit: "100% 100%",
            borderRadius: "4px",
          }}
        />
      );
    }

    // Handle Audio - Show as a small icon
    if (url.match(/\.(mp3|wav|aac|ogg|flac|m4a|wma|opus)$/i)) {
      return (
        <audio controls style={{ width: "100px" }} src={url}>
          Your browser does not support the audio tag.
        </audio>
      );
    }

    // Handle Video Thumbnail Previews
    if (url.match(/\.(mp4|webm|avi|mkv|mov|wmv|flv|mpeg|mpg|3gp)$/i)) {
      return (
        <video
          key={url}
          controls
          style={{
            width: "80%",
            height: "60px",
            objectFit: "cover",
            borderRadius: "4px",
          }}
        >
          <source src={url} />
          Your browser does not support the video tag.
        </video>
      );
    }

    return null;
  };

  return (
    <Box
      ref={drag}
      sx={{
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Typography
        sx={{
          color:
            question.row_index !== null && question.col_index !== null
              ? "#32618D"
              : "inherit",
        }}
      >
        {question.question_number}. {question.question_text}
      </Typography>
      {question.attachment && renderAttachment(question.attachment)}
      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Typography
          sx={{
            color:
              question.row_index !== null && question.col_index !== null
                ? "#32618D"
                : "inherit",
          }}
        >
          ഉത്തരം: {question.answer}
        </Typography>
        <ModeEditIcon
          onClick={handleEdit}
          sx={{ cursor: "pointer", color: "#B14E64" }}
        />
      </Box>
    </Box>
  );
};

function PlaceAnswer({ setClickedQuestion, setTabIndex }) {
  const [groupedQuestions, setGroupedQuestions] = useState({});

  const fetchQuestions = async () => {
    try {
      const response = await axios.post(`${API_URL}/questions_temp`, {
        CreatorTocken: localStorage.getItem("CreatorTocken"),
        game_id: localStorage.getItem("gameId"),
      });
      const grouped = response.data.reduce((acc, question) => {
        if (!acc[question.direction]) {
          acc[question.direction] = [];
        }
        acc[question.direction].push(question);
        return acc;
      }, {});

      // Sort questions within each direction group by question number
      for (let direction in grouped) {
        grouped[direction].sort(
          (a, b) => a.question_number - b.question_number,
        );
      }

      // Ensure 'across' appears before 'down'
      const orderedGrouped = {};
      if (grouped.across) orderedGrouped.across = grouped.across;
      if (grouped.down) orderedGrouped.down = grouped.down;

      setGroupedQuestions(orderedGrouped);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  useEffect(() => {
    // Call the function immediately
    fetchQuestions();

    // Set interval to fetch every 1 minute
    const intervalId = setInterval(() => {
      fetchQuestions();
    }, 1000); // 60000 ms = 1 minute

    // Cleanup interval when the component unmounts or dependency changes
    return () => clearInterval(intervalId);
  }, []); // This ensures interval respects changes to `isAnswersPlace`

  const directionMap = {
    across: "വലത്തോട്ട്",
    down: "താഴോട്ട്",
  };

  const handleDelete = async (questionNumber) => {
    const updatedAcross = groupedQuestions.across
      ? groupedQuestions.across.filter(
          (q) => q.question_number !== questionNumber,
        )
      : [];

    const updatedDown = groupedQuestions.down
      ? groupedQuestions.down.filter(
          (q) => q.question_number !== questionNumber,
        )
      : [];

    setGroupedQuestions({
      across: updatedAcross,
      down: updatedDown,
    });
    const response = await axios.post(`${API_URL}/delete_questions`, {
      CreatorTocken: localStorage.getItem("CreatorTocken"),
      game_id: localStorage.getItem("gameId"),
      questionNumber: questionNumber,
    });
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          height: "100vh",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        {/* Left Section - Across Questions */}
        <Box
          sx={{
            width: "25%",
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontFamily: "Roboto", fontWeight: 600 }}
          >
            വലത്തോട്ട്
          </Typography>
          {groupedQuestions.across &&
            groupedQuestions.across.map((question) => (
              <Box
                sx={{
                  border: "3px solid #000",
                  borderRadius: "8px",
                  padding: "8px",
                  position: "relative",
                }}
              >
                <Box
                  onClick={() => handleDelete(question.question_number)}
                  sx={{
                    position: "absolute",
                    top: "0px",
                    right: "0px",
                    cursor: "pointer",
                    color: "#B14E64",
                  }}
                >
                  <DeleteIcon />
                </Box>

                <DraggableQuestion
                  key={question.question_number}
                  question={question}
                  setClickedQuestion={setClickedQuestion}
                  setTabIndex={setTabIndex}
                />
              </Box>
            ))}
        </Box>

        {/* Center Section - Crossword Creator Grid */}
        <Box
          sx={{
            width: "50%",
            padding: "8px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            height: "100%",
            borderRight: "1px solid gray",
            borderLeft: "1px solid gray",
          }}
        >
          <CroswordCreatorGrid
            question={groupedQuestions}
            setGroupedQuestions={setGroupedQuestions}
          />
        </Box>

        {/* Right Section - Down Questions */}
        <Box
          sx={{
            width: "25%",
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontFamily: "Roboto", fontWeight: 600 }}
          >
            താഴോട്ട്
          </Typography>
          {groupedQuestions.down &&
            groupedQuestions.down.map((question) => (
              <Box
                sx={{
                  border: "3px solid #000",
                  borderRadius: "8px",
                  padding: "8px",
                  position: "relative",
                }}
              >
                <Box
                  onClick={() => handleDelete(question.question_number)}
                  sx={{
                    position: "absolute",
                    top: "0px",
                    right: "0px",
                    cursor: "pointer",
                    color: "#B14E64",
                  }}
                >
                  <DeleteIcon />
                </Box>
                <DraggableQuestion
                  key={question.question_number}
                  question={question}
                  setClickedQuestion={setClickedQuestion}
                  setTabIndex={setTabIndex}
                />
              </Box>
            ))}
        </Box>
      </Box>
    </DndProvider>
  );
}

export default PlaceAnswer;
